import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Divider,
  Chip,
  Fab,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import { useJournalApprovalRequests } from "src/hooks/journal-approval/useJournalApprovalRequests";
import StatusChip from "../general/StatusChip";
import {
  JournalApprovalDecision,
  JournalApprovalRequest,
  JournalApprovalResultOutput,
} from "models/journal-approval";
import { displayJournalType } from "src/util/helpers";

const decisionColor = (
  decision: JournalApprovalDecision
): "success" | "error" | "warning" | "default" => {
  if (decision === "APPROVED" || decision === "ALREADY_APPROVED") {
    return "success";
  }
  if (decision === "FAILED" || decision === "APPROVER_NOT_CONFIGURED") {
    return "error";
  }
  if (decision === "AMBIGUOUS" || decision === "NO_RECORD_FOUND") {
    return "warning";
  }
  return "default";
};

const JournalApprovals: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<string>(
    generateCurrentMonthValue()
  );
  const { requests, loading, fetchByMonth } = useJournalApprovalRequests();
  const navigate = useNavigate();
  const months = generateMonths();

  useEffect(() => {
    fetchByMonth(selectedMonth);
  }, [selectedMonth]);

  const handleMonthChange = (event: SelectChangeEvent) => {
    setSelectedMonth(event.target.value);
  };

  const groupedRecords = requests.reduce((groups, record) => {
    const date = record.createdAt.substring(0, 10);
    if (!groups[date]) groups[date] = [];
    groups[date].push(record);
    return groups;
  }, {} as { [date: string]: JournalApprovalRequest[] });

  return (
    <Container sx={{ pt: 4, pb: 8 }}>
      <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
        <IconButton
          onClick={() => navigate("/")}
          size="small"
          sx={{ alignSelf: "flex-start", flexGrow: 0 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1, textAlign: "center" }}>
          Journal Approval
        </Typography>
        <Box sx={{ flexGrow: 0, width: "48px" }} />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
        <Typography variant="h6" mr={2}>
          Select Month:
        </Typography>
        <Select
          value={selectedMonth}
          onChange={handleMonthChange}
          displayEmpty
          inputProps={{ "aria-label": "Select Month" }}
        >
          {months.map((month) => (
            <MenuItem key={month.value} value={month.value}>
              {month.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Typography variant="h5" gutterBottom>
        Journal Approval Records
      </Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : Object.keys(groupedRecords).length === 0 ? (
        <Typography>No journal approval records found</Typography>
      ) : (
        <Box>
          {Object.keys(groupedRecords).map((date) => (
            <Box key={date} sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {new Date(date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
              {groupedRecords[date].map((record) => {
                const output = record.output as JournalApprovalResultOutput;
                const typeLabel = displayJournalType(
                  output.recordType || record.input.recordType,
                  output.recordTypeLabel || record.input.recordTypeLabel
                );
                return (
                  <Box
                    key={record.requestId}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderBottom: "1px solid #ccc",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                    onClick={() =>
                      navigate(`/journal-approvals/JEAPP-${record.requestId}`)
                    }
                  >
                    <Box sx={{ flex: "0 0 172px", whiteSpace: "nowrap" }}>
                      <Typography variant="body1" noWrap>
                        JEAPP-{record.requestId}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: "1 1 50%", minWidth: 0, pr: 1 }}>
                      <Typography variant="body2" noWrap>
                        {[
                          `ID: ${output.journalId || record.input.journalId || "—"}`,
                          `Number: ${
                            output.journalTranId ||
                            record.input.journalTranId ||
                            record.input.journalNumber
                          }`,
                          typeLabel ? `Type: ${typeLabel}` : "",
                          `Requestor: ${
                            record.input.requesterName ||
                            record.input.requesterEmail ||
                            "—"
                          }`,
                        ]
                          .filter(Boolean)
                          .join(" - ")}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flex: "0 0 auto",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        pr: 0.5,
                      }}
                    >
                      <Chip
                        label={record.output.decision}
                        color={decisionColor(record.output.decision)}
                      />
                      <StatusChip status={record.status} />
                    </Box>
                    <Box
                      sx={{
                        flex: "0 0 160px",
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Typography variant="body2">
                        {new Date(record.createdAt).toLocaleString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                        })}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}

      <Fab
        color="primary"
        size="large"
        variant="extended"
        sx={{
          position: "fixed",
          bottom: 30,
          right: 24,
          zIndex: 1000,
        }}
        onClick={() => navigate("/journal-approvals/form")}
      >
        <AddIcon sx={{ mr: 1 }} />
        Create Journal Approvals
      </Fab>
    </Container>
  );
};

const generateCurrentMonthValue = (): string => {
  return new Date().toISOString().substring(0, 7);
};

const generateMonths = (): { value: string; label: string }[] => {
  const startMonth = new Date(2024, 7, 2, 12, 0, 0);
  const currentMonth = new Date();
  const months = [];
  for (
    let dt = new Date(startMonth);
    dt <= currentMonth;
    dt.setMonth(dt.getMonth() + 1)
  ) {
    const value = dt.toISOString().substring(0, 7);
    const label = dt.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
    months.push({ value, label });
  }
  return months.reverse();
};

export default JournalApprovals;
